/**
 * 裁剪（M3-11 / DESIGN.md §6.1）。
 *
 * 一次裁剪 = 一条历史：画布尺寸的结构命令 + 所有图层（含蒙版）像素平移的
 * 事务差分，经 dispatchWithPixels 合成提交。undo 一步全部还原。
 */
import type { Rect } from '../document/types';
import type { Layer } from '../document/types';
import { shiftLayerPixels, type ShiftTransaction } from '../engine/tile/shift';
import type { TileStore } from '../engine/tile/store';
import { createSetCanvasSizeCommand } from './commands/canvas-commands';
import { maskLayerIdFor } from './commands/mask';
import type { AppStore } from './store';

/**
 * 递归平移一整棵图层树（含蒙版）的像素——裁剪（本文件）与画布尺寸
 * （`app/canvas-size.ts`）共用同一份逻辑，只是 offset/newWidth/newHeight
 * 的算法不同（裁剪来自拖拽的矩形，画布尺寸来自锚点公式）。
 */
export function shiftAllLayerPixels(
  tiles: TileStore,
  layers: readonly Layer[],
  offsetX: number,
  offsetY: number,
  newWidth: number,
  newHeight: number,
  transaction: ShiftTransaction,
): Array<{ tx: number; ty: number }> {
  const dirty: Array<{ tx: number; ty: number }> = [];
  for (const layer of layers) {
    if (layer.mask !== undefined) {
      dirty.push(
        ...shiftLayerPixels(tiles, maskLayerIdFor(layer.id), offsetX, offsetY, newWidth, newHeight, transaction),
      );
    }
    if (layer.kind === 'group') {
      dirty.push(...shiftAllLayerPixels(tiles, layer.children, offsetX, offsetY, newWidth, newHeight, transaction));
    } else if (layer.kind === 'raster' || layer.kind === 'text') {
      dirty.push(...shiftLayerPixels(tiles, layer.id, offsetX, offsetY, newWidth, newHeight, transaction));
    }
  }
  return dirty;
}

/**
 * 把文档裁剪到 rect（文档空间，需与画布相交的正尺寸矩形）。
 * 返回是否执行（无文档/矩形非法时 false）。
 */
export function cropDocument(store: AppStore, rect: Rect): boolean {
  const document = store.document;
  if (document === null) return false;
  const width = Math.round(rect.width);
  const height = Math.round(rect.height);
  const x = Math.round(rect.x);
  const y = Math.round(rect.y);
  if (width < 1 || height < 1) return false;

  const transaction = store.beginPixelTransaction('裁剪');
  const dirty = shiftAllLayerPixels(store.tiles, document.root.children, x, y, width, height, transaction);

  store.setSelection(null); // 选区坐标随裁剪失义，直接取消
  store.dispatchWithPixels(createSetCanvasSizeCommand(width, height), transaction.end());
  store.invalidatePixels(dirty);
  return true;
}
